import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import type { BroadcastPreviewResult, BroadcastPayload } from '../types';
import type { NotificationChannel } from '@masters-club/types';
import { TelegramPreview, EmailPreview, AppPreview } from './channel-previews';

interface PreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  languageLabel: string;
  loading: boolean;
  error: string | null;
  result: BroadcastPreviewResult | null;
  channels: NotificationChannel[];
  payload: BroadcastPayload | null;
  mediaUrl?: string;
}

export function PreviewDialog({
  open,
  onOpenChange,
  languageLabel,
  loading,
  error,
  result,
  channels,
  payload,
  mediaUrl,
}: PreviewDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Предпросмотр сообщения</DialogTitle>
          <DialogDescription>
            Язык: {languageLabel}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {result && (
          <div className="space-y-6">
            {/* Каналы */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <div>
                <span className="text-sm text-muted-foreground">Получателей:</span>
                <span className="ml-2 font-medium">{result.recipientsCount}</span>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Каналы:</span>
                <span className="ml-2 flex items-center gap-1 inline-flex">
                  {channels.map((ch) => (
                    <Badge key={ch} variant="outline" className="text-xs">
                      {ch === 'TELEGRAM' && 'Telegram'}
                      {ch === 'EMAIL' && 'Email'}
                      {ch === 'APP' && 'In-App'}
                    </Badge>
                  ))}
                </span>
              </div>
            </div>

            {/* Превью по каналам */}
            <div className="space-y-4">
              {channels.includes('TELEGRAM') && (
                <TelegramPreview
                  message={result.message}
                  mediaUrl={mediaUrl}
                  buttons={payload?.telegram?.buttons}
                />
              )}
              {channels.includes('EMAIL') && (
                <EmailPreview
                  message={result.message}
                  buttons={payload?.email?.buttons}
                  html={payload?.email?.html}
                />
              )}
              {channels.includes('APP') && (
                <AppPreview
                  message={result.message}
                  buttons={payload?.app?.buttons}
                />
              )}
            </div>

            {/* Пример пользователя */}
            {result.sampleUser && (
              <div className="rounded-lg border p-4">
                <h4 className="text-sm font-medium mb-3">Пример пользователя:</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Имя:</span>
                    <span className="ml-2">{result.sampleUser.displayName}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Подписка:</span>
                    <span className="ml-2">{result.sampleUser.subscriptionStatus}</span>
                  </div>
                  {result.sampleUser.subscriptionPlanName && (
                    <div>
                      <span className="text-muted-foreground">План:</span>
                      <span className="ml-2">{result.sampleUser.subscriptionPlanName}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">Заказов:</span>
                    <span className="ml-2">{result.sampleUser.ordersCount}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Потрачено:</span>
                    <span className="ml-2">{result.sampleUser.totalSpent}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
